/**
 * Permission Helper Functions
 *
 * These functions provide the core logic for checking permissions throughout the application.
 * They use the centralized permission configuration as the single source of truth.
 */

import { UserRoleEnums } from "../Constant";
import { programEnum } from "../Helpers";
import {
  DEFAULT_ROUTES,
  FACILITY_ROLES,
  FACILITY_TYPE_RESTRICTIONS,
  ROUTE_PERMISSIONS,
  USER_TYPES,
} from "./permissions.config";

// ============================================================
// USER TYPE CLASSIFICATION
// ============================================================

/**
 * Determine the primary user type based on their roles
 * @param {Object} user - The authUser object
 * @returns {string} - USER_TYPES constant (SuperAdmin, Admin, TierUser, or FacilityUser)
 */
export function getUserType(user) {
  if (!user || !user.programs_roles) return null;

  const roles = user.programs_roles.map((pr) => pr.roles);

  // SuperAdmin takes highest priority
  if (roles.includes(UserRoleEnums.SUPER_ADMIN)) {
    return USER_TYPES.SUPER_ADMIN;
  }

  // Admin is next
  if (roles.includes(UserRoleEnums.ADMIN)) {
    return USER_TYPES.ADMIN;
  }

  // Tier User (Government officials)
  if (roles.includes(UserRoleEnums.TIER_USER)) {
    return USER_TYPES.TIER_USER;
  }

  // Everyone else is a facility user
  if (roles.some((role) => FACILITY_ROLES.includes(role))) {
    return USER_TYPES.FACILITY_USER;
  }

  return null;
}

/**
 * Check if user requires program selection before accessing the app
 * @param {Object} user - The authUser object
 * @returns {boolean}
 */
export function requiresProgramSelection(user) {
  if (!user) return false;

  const userType = getUserType(user);
  return userType === USER_TYPES.FACILITY_USER;
}

/**
 * Get the role for a specific program
 * @param {Object} user - The authUser object
 * @param {string} program - Program name (e.g., "GENERAL", "TB", "COVID-19")
 * @returns {string|null} - Role name or null
 */
export function getRoleForProgram(user, program) {
  if (!user || !user.programs_roles || !program) return null;

  const programRole = user.programs_roles.find((pr) => pr.programs === program);
  return programRole ? programRole.roles : null;
}

/**
 * Get all programs the user has access to
 * @param {Object} user - The authUser object
 * @returns {Array<string>} - Array of program names
 */
export function getUserPrograms(user) {
  if (!user || !user.programs_roles) return [];

  return user.programs_roles.map((pr) => pr.programs).filter(Boolean);
}

/**
 * Check if a program is valid for the user
 * @param {Object} user - The authUser object
 * @param {string} program - Program name
 * @returns {boolean}
 */
export function isValidProgramForUser(user, program) {
  if (!program) return false;

  const userPrograms = getUserPrograms(user);
  return userPrograms.includes(program);
}

// ============================================================
// DEFAULT ROUTE DETERMINATION
// ============================================================

/**
 * Get the default landing page for a user
 * @param {Object} user - The authUser object
 * @param {string|null} program - Current program (optional, required for facility users)
 * @returns {string} - Route path
 */
export function getDefaultRoute(user, program = null) {
    console.log("=== getDefaultRoute ===", { program, userType: getUserType(user) });

  if (!user) return "/signin";

  const userType = getUserType(user);

  switch (userType) {
    case USER_TYPES.SUPER_ADMIN:
      return DEFAULT_ROUTES[UserRoleEnums.SUPER_ADMIN];

    case USER_TYPES.ADMIN:
      return DEFAULT_ROUTES[UserRoleEnums.ADMIN];

    case USER_TYPES.TIER_USER: {
      const location = user.user_location;
      const tierRoutes = DEFAULT_ROUTES[UserRoleEnums.TIER_USER];
      return tierRoutes[location] || tierRoutes.default;
    }

    case USER_TYPES.FACILITY_USER: {
      if (!program) {
        // Facility users MUST have a program selected
        return "/select-program";
      }

      const role = getRoleForProgram(user, program);
      if (!role) return "/select-program";

      // Check if it's ESPN program
      if (program === programEnum.ESPN) {
        return (
          DEFAULT_ROUTES.FACILITY_USERS[programEnum.ESPN][role] ||
          "/espn/dashboard"
        );
      }

      // Check if it's a defined program
      if (DEFAULT_ROUTES.FACILITY_USERS[program]) {
        return DEFAULT_ROUTES.FACILITY_USERS[program][role];
      }

      // Otherwise it's a public health program
      return (
        DEFAULT_ROUTES.FACILITY_USERS.PUBLIC_HEALTH[role] ||
        "/programs/ph-manage-sample"
      );
    }

    default:
      return "/signin";
  }
}

// ============================================================
// ROUTE PERMISSIONS
// ============================================================

/**
 * Check if user can access a specific route
 * @param {Object} user - The authUser object
 * @param {string} route - Route path
 * @param {string|null} program - Current program (optional)
 * @returns {boolean}
 */
export function canAccessRoute(user, route, program = null) {
    console.log("=== canAccessRoute ===", { route, program, userType: getUserType(user) });

  if (!user || !route) return false;

  // Check if route is in permissions config
  const allowedRoles = ROUTE_PERMISSIONS[route];

  if (!allowedRoles) {
    // If route not in config, allow by default (can be changed to deny by default)
    return true;
  }

  // '*' means all authenticated users can access
  if (allowedRoles.includes("*")) {
    return true;
  }

  const userType = getUserType(user);

  // SuperAdmin can access everything
  if (userType === USER_TYPES.SUPER_ADMIN) {
    return true;
  }

  // For facility users, check role for current program
  if (userType === USER_TYPES.FACILITY_USER) {
    if (!program) return false;
    const role = getRoleForProgram(user, program);
    return role && allowedRoles.includes(role);
  }

  // For Admin and Tier users, check their primary role
  const roles = user.programs_roles?.map((pr) => pr.roles) || [];
  return roles.some((role) => allowedRoles.includes(role));
}

/**
 * Check if user can access a route based on facility type
 * @param {Object} user - The authUser object
 * @param {string} route - Route path
 * @returns {boolean}
 */
export function canAccessByFacilityType(user, route) {
  if (!user) return false;

  const restrictions = FACILITY_TYPE_RESTRICTIONS[route];
  if (!restrictions) return true; // No restrictions

  return restrictions.includes(user.facility_type);
}

// ============================================================
// ROLE CHECKING UTILITIES
// ============================================================

/**
 * Check if user has a specific role (in any program)
 * @param {Object} user - The authUser object
 * @param {string} role - Role to check for
 * @returns {boolean}
 */
export function hasRole(user, role) {
  if (!user || !user.programs_roles) return false;

  return user.programs_roles.some((pr) => pr.roles === role);
}

/**
 * Check if user has any of the specified roles
 * @param {Object} user - The authUser object
 * @param {...string} roles - Roles to check for
 * @returns {boolean}
 */
export function hasAnyRole(user, ...roles) {
  if (!user || !user.programs_roles) return false;

  return roles.some((role) => hasRole(user, role));
}

/**
 * Check if user has a specific role for the current program
 * @param {Object} user - The authUser object
 * @param {string} program - Program name
 * @param {string} role - Role to check for
 * @returns {boolean}
 */
export function hasRoleInProgram(user, program, role) {
  const userRole = getRoleForProgram(user, program);
  return userRole === role;
}

/**
 * Check if user has any of the specified roles for the current program
 * @param {Object} user - The authUser object
 * @param {string} program - Program name
 * @param {...string} roles - Roles to check for
 * @returns {boolean}
 */
export function hasAnyRoleInProgram(user, program, ...roles) {
  const userRole = getRoleForProgram(user, program);
  return roles.includes(userRole);
}

// ============================================================
// MENU PERMISSIONS (to be used by Sidebar)
// ============================================================

/**
 * Filter menu items based on user permissions
 * @param {Array} menuItems - Array of menu item objects
 * @param {Object} user - The authUser object
 * @param {string|null} program - Current program
 * @returns {Array} - Filtered menu items
 */
export function filterMenuItems(menuItems, user, program = null) {
  if (!menuItems || !user) return [];

  return menuItems.filter((item) => {
    // Check if user has required roles for this menu item
    if (item.roles) {
      if (item.roles.includes("*")) return true; // Available to all

      const userType = getUserType(user);

      if (userType === USER_TYPES.SUPER_ADMIN) return true; // SuperAdmin sees everything

      if (userType === USER_TYPES.FACILITY_USER && program) {
        const role = getRoleForProgram(user, program);
        return role && item.roles.includes(role);
      }

      const roles = user.programs_roles?.map((pr) => pr.roles) || [];
      return roles.some((role) => item.roles.includes(role));
    }

    return true; // No role requirement
  });
}

// ============================================================
// VALIDATION HELPERS
// ============================================================

/**
 * Validate program selection before allowing access
 * @param {Object} user - The authUser object
 * @param {string|null} program - Selected program
 * @returns {Object} - { isValid: boolean, error: string|null, shouldRedirect: string|null }
 */
export function validateProgramSelection(user, program) {
  if (!user) {
    return {
      isValid: false,
      error: "User not authenticated",
      shouldRedirect: "/signin",
    };
  }

  const userType = getUserType(user);

  // Non-facility users don't need program selection
  if (userType !== USER_TYPES.FACILITY_USER) {
    return { isValid: true, error: null, shouldRedirect: null };
  }

  // Facility users MUST have a program
  if (!program) {
    return {
      isValid: false,
      error: "Program selection required",
      shouldRedirect: "/select-program",
    };
  }

  // Validate program is valid for user
  if (!isValidProgramForUser(user, program)) {
    return {
      isValid: false,
      error: "Invalid program for user",
      shouldRedirect: "/select-program",
    };
  }

  return { isValid: true, error: null, shouldRedirect: null };
}

// ============================================================
// EXPORT ALL
// ============================================================

export default {
  getUserType,
  requiresProgramSelection,
  getRoleForProgram,
  getUserPrograms,
  isValidProgramForUser,
  getDefaultRoute,
  canAccessRoute,
  canAccessByFacilityType,
  hasRole,
  hasAnyRole,
  hasRoleInProgram,
  hasAnyRoleInProgram,
  filterMenuItems,
  validateProgramSelection,
};
